import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { useCommandBarStore } from '~/stores/command-bar.store';

/** A single keyboard-shortcut row in the cheat-sheet. */
interface ShortcutRow {
  keys: string[];
  description: string;
}

/** Logical sections used to group shortcuts in the cheat-sheet. */
interface ShortcutSection {
  title: string;
  rows: ShortcutRow[];
}

const SECTIONS: ShortcutSection[] = [
  {
    title: 'General',
    rows: [
      { keys: ['⌘', 'K'], description: 'Open command palette' },
      { keys: ['⌘', '/'], description: 'Show this cheat-sheet' },
      { keys: ['Esc'], description: 'Close current dialog or palette' },
    ],
  },
  {
    title: 'Navigation',
    rows: [
      { keys: ['G', 'D'], description: 'Go to Dashboard' },
      { keys: ['G', 'A'], description: 'Go to Activities' },
      { keys: ['G', 'P'], description: 'Go to Projects' },
      { keys: ['G', 'C'], description: 'Open Copilot' },
    ],
  },
  {
    title: 'Command palette',
    rows: [
      { keys: ['↑', '↓'], description: 'Move selection' },
      { keys: ['↵'], description: 'Run selected action' },
      { keys: ['⌫'], description: 'Back to root list (when in sub-page)' },
    ],
  },
];

/** Renders a single key chip — small monospace pill. */
function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="bg-muted text-muted-foreground inline-flex h-6 min-w-6 items-center justify-center rounded border px-1.5 font-mono text-xs">
      {children}
    </kbd>
  );
}

/**
 * Modal cheat-sheet of available keyboard shortcuts. Triggered from the
 * command bar (Productivity → Show keyboard shortcuts) or via `Cmd+/`.
 *
 * The shortcuts shown here are aspirational — the only one currently wired
 * up globally is `Cmd+K`. The other rows document conventions we plan to
 * implement and serve as a discoverable reference.
 */
export function KeyboardShortcutsDialog() {
  const open = useCommandBarStore((s) => s.shortcutsDialogOpen);
  const close = useCommandBarStore((s) => s.closeShortcutsDialog);

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? close() : null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Get around the app faster.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h3 className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
                {section.title}
              </h3>
              <ul className="flex flex-col gap-1.5">
                {section.rows.map((row) => (
                  <li key={row.description} className="flex items-center justify-between text-sm">
                    <span>{row.description}</span>
                    <span className="flex items-center gap-1">
                      {row.keys.map((key) => (
                        <Key key={key}>{key}</Key>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
